import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Send, WifiOff } from "lucide-react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { errorDev } from '../utils/logger.js';

const INITIAL_FORM = {
  name: "",
  email: "",
  location: "",
  desiredRole: "",
  challenge: "",
  notes: "",
};

const ARCHETYPES = [
  {
    title: "1. The Root-System Thinker",
    body:
      "You don't see a \"leaf\"; you see the entire forest's respiration. You understand that a line of code in our app affects a soil sensor, which affects a farmer's livelihood, which affects a family's dinner table. Are you someone who fixes the \"leak\" by redesigning the whole pipe?",
  },
  {
    title: "2. The Uncertainty Buffer",
    body:
      "When the storm hits, you don't find shelter you build a windmill. Agriculture is a battle against the unpredictable. We need minds that stay logical when the data shifts. You don't panic; you pivot. When the \"impossible\" happens, do you see a dead end or a new laboratory?",
  },
  {
    title: "3. The Data Whisperer",
    body:
      "You hear the \"click\" before the wheel falls off. You find the \"ghosts\" in the numbers. You notice a 1% dip in soil moisture before the crop even wilts. You provide the Certainty in our Cycle. Do you see stories where others just see spreadsheets?",
  },
  {
    title: "4. The Bio-Empathy Specialist",
    body:
      "You treat the Earth like a teammate, not a resource. Technology should serve the dirt, not dominate it. We hire humans who care about the oxygen they breathe and the soil they walk on. Do you make decisions based on the health of the planet as if it were your own?",
  },
  {
    title: "5. The Century Visionary",
    body:
      "You plant trees whose shade you will never sit in. Most companies think about next quarter; we think about the next century. We build systems our great-grandchildren will still be using. Are you ready to work on a legacy that outlives your career?",
  },
];

function FormField({ id, label, required, error, children }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-gray-900 dark:text-white">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </label>
      {children}
      {error ? <p className="mt-1 text-xs text-rose-600 dark:text-rose-300">{error}</p> : null}
    </div>
  );
}

export default function Careers() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const formRef = useRef(null);

  const sectionLinks = useMemo(
    () => [
      { id: "orbit", label: "Orbit" },
      { id: "who-we-look-for", label: "Who We Look For" },
      { id: "archetypes", label: "Archetypes" },
      { id: "challenge", label: "Challenge" },
      { id: "application", label: "Application" },
    ],
    []
  );

  const introCards = useMemo(
    () => [
      {
        title: "Who We Look For",
        body: "Orrith - Cycle of Certainty does not hire resumes. We look for people whose instincts align with our mission. People who notice problems others walk past, who build solutions before being asked, and who treat every human interaction as an opportunity to leave things better than they found them.",
      },
      {
        title: "How We Work",
        body: "We operate with the seriousness of institutions that protect nations: disciplined thinking, clear communication, operational integrity. But in service of life, not power. The problems we work on are among the most consequential on Earth: food security, climate adaptation, economic dignity for farmers, and the elimination of preventable waste.",
      },
      {
        title: "Character Over Credentials",
        body: "Your grades matter less than your character. Your pedigree matters less than your persistence. We have team members who came from elite universities and team members who taught themselves to code on a borrowed phone. What they share is a refusal to look away when systems fail the people they serve.",
      },
      {
        title: "Human Review Always",
        body: "Every application is read by a human. We do not use automated screening. We do not filter by keyword. We read what you wrote, think about what it means, and respond with the respect your effort deserves.",
      },
    ],
    []
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const inputClass = useCallback(
    (field) =>
      `w-full rounded-lg border px-4 py-2.5 text-sm text-gray-900 dark:text-white bg-white dark:bg-neutral-900 placeholder:text-gray-400 dark:placeholder:text-gray-500 transition focus:outline-none focus:ring-2 ${
        errors[field]
          ? "border-rose-400 focus:ring-rose-400"
          : "border-gray-300 dark:border-neutral-700 focus:ring-green-500"
      }`,
    [errors]
  );

  const scrollToSection = useCallback((id) => {
    const node = document.getElementById(id);
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const onChange = useCallback(
    (field) => (event) => {
      const value = event.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: "" }));
      setSubmitError("");
    },
    []
  );

  const validate = useCallback(() => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Full Name is required.";
    if (!form.email.trim()) nextErrors.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(form.email.trim())) nextErrors.email = "Enter a valid email address.";
    if (!form.location.trim()) nextErrors.location = "Location / Timezone is required.";
    if (!form.desiredRole.trim()) nextErrors.desiredRole = "Tell us what kind of work you see yourself doing.";
    if (!form.challenge.trim()) nextErrors.challenge = "Challenge response is required.";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [form]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!validate()) {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      setSubmitting(true);
      setSubmitError("");

      try {
        await addDoc(collection(db, "careerApplications"), {
          name: form.name.trim(),
          email: form.email.trim(),
          location: form.location.trim(),
          desiredRole: form.desiredRole.trim(),
          challenge: form.challenge.trim(),
          notes: form.notes.trim(),
          status: "new",
          source: "careers_page",
          submittedAt: serverTimestamp(),
        });

        setSubmitted(true);
        setForm(INITIAL_FORM);
      } catch (err) {
        errorDev("Operation failed:", err);
        setSubmitError("Submission failed to enter the Orbit. Please check your connection and try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [form, validate]
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 pb-16">
      <div className="sticky top-0 z-30 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex items-center justify-between h-12">
            <h1 className="text-lg font-bold tracking-wide text-green-800 dark:text-green-400 uppercase">Careers</h1>
            {!isOnline ? (
              <div className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                <WifiOff className="h-3.5 w-3.5" />
                Offline
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-4 sm:gap-5">
          <aside className="lg:sticky lg:top-20 self-start">
            <div className="rounded-2xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-black p-3 shadow-sm">
              <p className="px-1 pb-2 text-xs font-semibold tracking-wide text-gray-500 dark:text-gray-400">On this page</p>
              <div className="flex gap-2 overflow-x-auto lg:block lg:space-y-1 lg:overflow-visible pb-1 lg:pb-0 no-scrollbar">
                {sectionLinks.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => scrollToSection(item.id)}
                    className="shrink-0 lg:w-full rounded-lg px-3 lg:px-2.5 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-900 transition"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <div className="space-y-6">
            <section id="orbit" className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-5 sm:p-6">
              <h2 className="text-balance text-3xl font-black leading-tight tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl text-center">
                Join the Orbit of Orrith
              </h2>

              <div className="mx-auto mt-6 max-w-4xl text-center">
                <p className="text-base leading-relaxed text-gray-700 dark:text-gray-200 sm:text-lg">
                  We aren't looking for employees. We are inviting talented people to join us as Architects of the Cycle. At Orrith, we don't just work in Agriculture and Environment. We are engineering the Cycle of Certainty a living system where nature's rhythm and human precision sync perfectly to eliminate waste and guarantee life.
                </p>
                <p className="mt-4 text-base leading-relaxed text-gray-600 dark:text-gray-300">
                  We look less at your past and more at your Cognitive Architecture. We are searching for uncommon thinkers whose minds operate on a different frequency and who build solutions with discipline, empathy, and long-range vision.
                </p>
              </div>
            </section>

            <section id="who-we-look-for" className="grid gap-4 sm:grid-cols-2">
              {introCards.map((card) => (
                <article key={card.title} className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-5 shadow-sm">
                  <h2 className="text-base font-bold text-gray-900 dark:text-white sm:text-lg">{card.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-gray-700 dark:text-gray-200">{card.body}</p>
                </article>
              ))}
            </section>

            <section id="archetypes" className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-5 sm:p-6">
              <h2 className="text-2xl font-black text-gray-900 dark:text-white sm:text-3xl">The 5 Archetypes of the Orrith Mind</h2>
              <p className="mt-2 text-sm font-medium uppercase tracking-wide text-gray-600 dark:text-gray-300">Do you see yourself in these uncommon traits?</p>
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {ARCHETYPES.map((item) => (
                  <article key={item.title} className="rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 p-4">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white sm:text-base">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-700 dark:text-gray-200">{item.body}</p>
                  </article>
                ))}
              </div>
            </section>

            <section id="challenge" className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-5 sm:p-6">
              <h2 className="text-2xl font-black text-gray-900 dark:text-white sm:text-3xl">The Entry Challenge</h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">There is no right answer. We want to see how you think.</p>
              <div className="mt-4 rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 p-4 text-sm leading-relaxed text-gray-700 dark:text-gray-200 sm:p-5">
                <p>
                  You are monitoring Orrith - Cycle of Certainty's NorthStar dashboard at 9:00 AM on a Monday. In District Musanze, tomato surplus on Farm Marketplace has spiked 300% this week, with listings doubling and sellers cutting prices. In the neighboring District Rubavu, tomato prices are climbing steadily.
                </p>
                <p className="mt-3">
                  The weekly market intelligence signal goes out to 2,000 farmers in 10 minutes. You have to write that signal.
                </p>
                <p className="mt-3 font-bold text-gray-900 dark:text-white">What do you say, and just as importantly, what do you choose not to say?</p>
              </div>
            </section>

            <section id="application" ref={formRef} className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-5 text-gray-900 dark:text-white shadow-sm sm:p-6">
              <h2 className="text-2xl font-black sm:text-3xl">Application</h2>

              {submitted ? (
                <div className="mt-5 rounded-xl border border-emerald-300 bg-emerald-50 p-4 dark:border-emerald-700 dark:bg-emerald-900/30" role="status" aria-live="polite">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600 dark:text-emerald-300" />
                    <div>
                      <p className="font-semibold text-emerald-800 dark:text-emerald-200">Your application has entered the Orbit of Orrith. Thank you.</p>
                      <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-300">Every application is reviewed by a human, and we will respond with respect for your effort.</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="mt-4 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800 dark:bg-emerald-600 dark:hover:bg-emerald-500"
                  >
                    Submit Another Application
                  </button>
                </div>
              ) : (
                <>
                  {submitError ? (
                    <div className="mt-5 rounded-xl border border-rose-300 bg-rose-50 p-4 dark:border-rose-700 dark:bg-rose-900/25" role="alert" aria-live="assertive">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="mt-0.5 h-5 w-5 text-rose-600 dark:text-rose-300" />
                        <p className="text-sm font-medium text-rose-700 dark:text-rose-200">{submitError}</p>
                      </div>
                    </div>
                  ) : null}

                  <form className="mt-6 space-y-4" noValidate onSubmit={handleSubmit}>
                    <FormField id="career-name" label="Full Name" required error={errors.name}>
                      <input id="career-name" type="text" autoComplete="name" value={form.name} onChange={onChange("name")} className={inputClass("name")} placeholder="Your full name" />
                    </FormField>

                    <FormField id="career-email" label="Email" required error={errors.email}>
                      <input id="career-email" type="email" autoComplete="email" value={form.email} onChange={onChange("email")} className={inputClass("email")} placeholder="you@example.com" />
                    </FormField>

                    <FormField id="career-location" label="Location / Timezone" required error={errors.location}>
                      <input id="career-location" type="text" value={form.location} onChange={onChange("location")} className={inputClass("location")} placeholder="Kigali, Rwanda (UTC+2)" />
                    </FormField>

                    <FormField id="career-role" label="What kind of work do you see yourself doing?" required error={errors.desiredRole}>
                      <textarea id="career-role" rows={4} value={form.desiredRole} onChange={onChange("desiredRole")} className={`${inputClass("desiredRole")} resize-y`} placeholder="Engineering, field operations, design, data science, strategy, or something we have not named yet." />
                    </FormField>

                    <FormField id="career-challenge" label="Your response to the challenge above" required error={errors.challenge}>
                      <textarea id="career-challenge" rows={7} value={form.challenge} onChange={onChange("challenge")} className={`${inputClass("challenge")} resize-y`} placeholder="Write the signal you would send, and what you would intentionally leave unsaid." />
                    </FormField>

                    <FormField id="career-notes" label="Anything else?" error={errors.notes}>
                      <textarea id="career-notes" rows={4} value={form.notes} onChange={onChange("notes")} className={`${inputClass("notes")} resize-y`} placeholder="Portfolio links, constraints, context, or anything else you want us to understand." />
                    </FormField>

                    <button
                      type="submit"
                      disabled={submitting}
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-lg bg-green-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-white dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-offset-neutral-900 sm:w-auto ${submitting ? "cursor-not-allowed opacity-65" : ""
                        }`}
                    >
                      {submitting ? (
                        <>
                          <div className="orrith-loader orrith-loader-xs orrith-loader-on-dark" />
                          Sending application...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Submit
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </section>

            <p className="text-center text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              We are an equal opportunity organization. Every application is considered on its merits, regardless of race, religion, gender identity, sexual orientation, national origin, disability status, or educational background. The soil does not ask who planted the seed. Neither do we.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}